import { useState, useEffect } from 'react';

interface SplitPanelLayoutProps {
  mainContent: React.ReactNode;
  sidePanel?: React.ReactNode;
  showSidePanel?: boolean;
}

export function SplitPanelLayout({
  mainContent,
  sidePanel,
  showSidePanel = false,
}: SplitPanelLayoutProps) {
  // Chat panel state persists across slide changes via localStorage
  const [isPanelOpen, setIsPanelOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    const saved = localStorage.getItem('chatPanelOpen');
    return saved === 'true';
  });

  useEffect(() => {
    localStorage.setItem('chatPanelOpen', String(isPanelOpen));
  }, [isPanelOpen]);

  const SIDEBAR_WIDTH = '420px'; // Cursor-style fixed right sidebar width

  return (
    <div className="flex flex-row gap-0 flex-1 min-h-0 relative">
      {/* Main content area - always visible, takes remaining space */}
      <div
        className="flex-1 min-w-0 transition-all duration-300 overflow-auto"
      >
        {mainContent}
      </div>

      {/* Right sidebar - chat (fixed width when open, like Cursor) */}
      {showSidePanel && (
        <div
          className={`
            flex-shrink-0 flex flex-col
            transition-all duration-300 ease-in-out
            bg-white dark:bg-slate-800 border-l border-slate-200 dark:border-slate-700
            overflow-hidden
            ${isPanelOpen ? 'opacity-100' : 'w-0 opacity-0 overflow-hidden'}
          `}
          style={isPanelOpen ? { width: SIDEBAR_WIDTH, minWidth: SIDEBAR_WIDTH } : undefined}
        >
          <div className="h-full flex flex-col min-w-0">
            {/* Panel header - compact bar with close */}
            <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-700 flex items-center justify-end flex-shrink-0">
              <button
                onClick={() => setIsPanelOpen(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700"
                aria-label="Close chat panel"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Panel content - ChatPanel fills and scrolls */}
            <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
              {sidePanel || (
                <div className="text-center text-slate-500 dark:text-slate-400 mt-8 px-6">
                  <svg className="w-10 h-10 mx-auto mb-4 text-slate-300 dark:text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                  <p className="text-sm font-medium mb-1">Learning Companion</p>
                  <p className="text-xs text-slate-400 dark:text-slate-500">
                    Chat with your AI companion about the content
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Floating toggle button (when panel is available but closed) */}
      {showSidePanel && !isPanelOpen && (
        <button
          onClick={() => setIsPanelOpen(true)}
          className="fixed right-6 bottom-6 lg:right-8 lg:bottom-8
                     accent-bg hover:opacity-90 text-white
                     rounded-full p-4 shadow-lg transition-all
                     hover:scale-110 z-50 group"
          aria-label="Open learning companion"
          title="Open learning companion"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
            />
          </svg>
          {/* Tooltip */}
          <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2
                          bg-slate-900 text-white text-sm px-3 py-1 rounded
                          opacity-0 group-hover:opacity-100 transition-opacity
                          whitespace-nowrap pointer-events-none">
            Open Chat
          </span>
        </button>
      )}
    </div>
  );
}
